import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ExternalLinkIcon, MessageSquareIcon, UsersIcon, TrendingUpIcon, AlertCircleIcon, CheckCircle2Icon, SparklesIcon, DatabaseIcon, FileTextIcon, BriefcaseIcon, BuildingIcon, MailIcon, PhoneIcon, MapPinIcon, FolderIcon, CalendarIcon, XIcon } from 'lucide-react';
import { seedData } from '../data/seedData';
import { useActivityFeed } from '../lib/activityFeed';
interface AccountOverviewProps {
  accountId: string;
  onNavigate: (section: string) => void;
}
export function AccountOverview({
  accountId,
  onNavigate
}: AccountOverviewProps) {
  console.log('[AccountOverview] Component mounting');
  console.log('[AccountOverview] Received accountId:', accountId);
  console.log('[AccountOverview] accountId type:', typeof accountId);
  const account = seedData.accounts.find(a => a.id === accountId);
  const {
    addActivity
  } = useActivityFeed();
  console.log('[AccountOverview] Searching for account in seedData.accounts');
  console.log('[AccountOverview] Total accounts:', seedData.accounts.length);
  console.log('[AccountOverview] Available account IDs:', seedData.accounts.map(a => a.id));
  console.log('[AccountOverview] Found account:', account ? account.name : 'NOT FOUND');
  const [activeTab, setActiveTab] = useState<'overview' | 'team' | 'assets'>('overview');
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  if (!account) {
    console.error('[AccountOverview] Account not found!');
    console.error('[AccountOverview] Searched for accountId:', accountId);
    console.error('[AccountOverview] This will render the "not found" message');
    return <div className="text-center py-12" data-id="element-1544">
        <p className="text-slate-400" data-id="element-1545">Account not found</p>
        <p className="text-slate-500 text-sm mt-2" data-id="element-1546">Searched for: {accountId}</p>
      </div>;
  }
  console.log('[AccountOverview] Account found successfully:', {
    id: account.id,
    name: account.name
  });
  // Add some realistic Quad activities on component mount
  useEffect(() => {
    if (account) {
      const sampleActivities = [{
        type: 'order' as const,
        message: 'Print job #2847 completed - 50,000 direct mail pieces'
      }, {
        type: 'milestone' as const,
        message: 'Q1 catalog campaign launched successfully'
      }, {
        type: 'order' as const,
        message: 'Quote request approved for holiday marketing materials'
      }, {
        type: 'insight' as const,
        message: 'Production efficiency up 12% this quarter'
      }, {
        type: 'system' as const,
        message: 'Material shipment departed from Chicago facility'
      }, {
        type: 'milestone' as const,
        message: 'Quality check passed for store signage batch'
      }, {
        type: 'order' as const,
        message: 'Rush order processed - 48hr turnaround confirmed'
      }, {
        type: 'people' as const,
        message: 'New team member onboarded to account'
      }];
      // Add activities with delays to simulate real activity
      sampleActivities.forEach((activity, index) => {
        setTimeout(() => {
          addActivity(account.id, activity);
        }, index * 500);
      });
    }
  }, [account?.id, addActivity]);
  const healthColors = {
    Green: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40 shadow-emerald-500/20',
    Yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40 shadow-yellow-500/20',
    Red: 'bg-red-500/20 text-red-400 border-red-500/40 shadow-red-500/20'
  };
  const externalBot = account.bots.find(b => b.type === 'external');
  const internalBot = account.bots.find(b => b.type === 'internal');
  const quadTeam = account.people?.filter(p => p.organization === 'Quad') || [];
  const clientContacts = account.people?.filter(p => p.organization === 'Client') || [];
  return <>
      <div className="space-y-6" data-id="element-1547">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="relative" data-id="element-1548">
          <button onClick={() => onNavigate('accounts')} className="group flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4" data-id="element-1549">
            <ArrowLeftIcon className="w-4 h-4 transition-transform group-hover:-translate-x-1" data-id="element-1550" />
            <span className="text-sm font-medium" data-id="element-1551">Back to Accounts</span>
          </button>
          <div className="bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl overflow-hidden shadow-2xl shadow-black/30" data-id="element-1552">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" data-id="element-1553" />
            <div className="relative p-8" data-id="element-1554">
              <div className="flex items-start justify-between gap-6 mb-6" data-id="element-1555">
                <div className="flex items-start gap-6 flex-1" data-id="element-1556">
                  <div className="w-32 h-16 flex-shrink-0 bg-white/95 rounded-xl p-3 shadow-xl ring-1 ring-slate-700/20" data-id="element-1557">
                    <img src={account.logo_url} alt={account.name} className="w-full h-full object-contain" data-id="element-1558" />
                  </div>
                  <div className="flex-1" data-id="element-1559">
                    <h1 className="text-4xl font-bold text-white mb-2 tracking-tight" data-id="element-1560">
                      {account.name}
                    </h1>
                    <div className="flex items-center gap-3 text-slate-400 mb-4" data-id="element-1561">
                      <span className="font-medium" data-id="element-1562">{account.industry}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-600" data-id="element-1563" />
                      <span className="font-medium" data-id="element-1564">{account.region}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-600" data-id="element-1565" />
                      <span className="text-xs" data-id="element-1566">
                        Last sync:{' '}
                        {new Date(account.last_sync).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1567">
                      {account.products_services.map(service => <span key={service} className="px-3 py-1.5 text-xs font-medium bg-slate-800/60 text-slate-300 rounded-lg border border-slate-700/40" data-id="element-1568">
                          {service}
                        </span>)}
                    </div>
                  </div>
                </div>
                <div className={`px-4 py-2 rounded-xl text-sm font-bold border shadow-lg ${healthColors[account.health]}`} data-id="element-1569">
                  {account.health}
                </div>
              </div>
              {/* KPIs */}
              <div className="grid grid-cols-3 gap-4" data-id="element-1570">
                <div className="group relative bg-gradient-to-br from-slate-950/80 to-slate-900/80 border border-slate-800/60 rounded-xl p-5 hover:border-blue-500/40 transition-all" data-id="element-1571">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1572" />
                  <div className="relative flex items-center justify-between" data-id="element-1573">
                    <div data-id="element-1574">
                      <p className="text-xs text-slate-500 mb-1 font-semibold uppercase tracking-wider" data-id="element-1575">
                        Active Jobs
                      </p>
                      <p className="text-3xl font-bold text-white" data-id="element-1576">
                        {account.kpis.active_jobs}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center" data-id="element-1577">
                      <TrendingUpIcon className="w-6 h-6 text-blue-400" data-id="element-1578" />
                    </div>
                  </div>
                </div>
                <div className="group relative bg-gradient-to-br from-slate-950/80 to-slate-900/80 border border-slate-800/60 rounded-xl p-5 hover:border-green-500/40 transition-all" data-id="element-1579">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1580" />
                  <div className="relative flex items-center justify-between" data-id="element-1581">
                    <div data-id="element-1582">
                      <p className="text-xs text-slate-500 mb-1 font-semibold uppercase tracking-wider" data-id="element-1583">
                        Open Quotes
                      </p>
                      <p className="text-3xl font-bold text-white" data-id="element-1584">
                        {account.kpis.open_quotes}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center" data-id="element-1585">
                      <CheckCircle2Icon className="w-6 h-6 text-green-400" data-id="element-1586" />
                    </div>
                  </div>
                </div>
                <div className="group relative bg-gradient-to-br from-slate-950/80 to-slate-900/80 border border-slate-800/60 rounded-xl p-5 hover:border-red-500/40 transition-all" data-id="element-1587">
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1588" />
                  <div className="relative flex items-center justify-between" data-id="element-1589">
                    <div data-id="element-1590">
                      <p className="text-xs text-slate-500 mb-1 font-semibold uppercase tracking-wider" data-id="element-1591">
                        SLA Alerts
                      </p>
                      <p className={`text-3xl font-bold ${account.kpis.sla_alerts > 0 ? 'text-red-400' : 'text-green-400'}`} data-id="element-1592">
                        {account.kpis.sla_alerts}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${account.kpis.sla_alerts > 0 ? 'bg-red-500/10' : 'bg-green-500/10'} flex items-center justify-center`} data-id="element-1593">
                      <AlertCircleIcon className={`w-6 h-6 ${account.kpis.sla_alerts > 0 ? 'text-red-400' : 'text-green-400'}`} data-id="element-1594" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-800/60" data-id="element-1595">
          <button onClick={() => setActiveTab('overview')} className={`px-6 py-3 text-sm font-semibold transition-all relative ${activeTab === 'overview' ? 'text-white' : 'text-slate-400 hover:text-white'}`} data-id="element-1596">
            Overview
            {activeTab === 'overview' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500" data-id="element-1597" />}
          </button>
          <button onClick={() => setActiveTab('team')} className={`px-6 py-3 text-sm font-semibold transition-all relative ${activeTab === 'team' ? 'text-white' : 'text-slate-400 hover:text-white'}`} data-id="element-1598">
            Team
            {activeTab === 'team' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500" data-id="element-1599" />}
          </button>
          <button onClick={() => setActiveTab('assets')} className={`px-6 py-3 text-sm font-semibold transition-all relative ${activeTab === 'assets' ? 'text-white' : 'text-slate-400 hover:text-white'}`} data-id="element-1600">
            Assets
            {activeTab === 'assets' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500" data-id="element-1601" />}
          </button>
        </div>
        {/* Tab Content */}
        {activeTab === 'overview' && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-6" data-id="element-1602">
            {/* Primary Owner */}
            <div className="bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 border border-slate-800/80 rounded-xl p-6 shadow-lg" data-id="element-1603">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-1604">
                <BriefcaseIcon className="w-5 h-5 text-blue-400" data-id="element-1605" />
                Primary Quad Owner
              </h3>
              <div className="flex items-center gap-4" data-id="element-1606">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white text-xl font-bold shadow-lg" data-id="element-1607">
                  {account.primary_owner.name.charAt(0)}
                </div>
                <div className="flex-1" data-id="element-1608">
                  <p className="text-lg font-semibold text-white" data-id="element-1609">
                    {account.primary_owner.name}
                  </p>
                  <p className="text-sm text-slate-400" data-id="element-1610">
                    {account.primary_owner.role}
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-1611">
                    {account.primary_owner.email}
                  </p>
                </div>
              </div>
            </div>
            {/* Bots */}
            <div className="space-y-4" data-id="element-1612">
              <h3 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-1613">
                <MessageSquareIcon className="w-5 h-5 text-purple-400" data-id="element-1614" />
                Chatbot Assistants
              </h3>
              {externalBot && <div className="group relative bg-gradient-to-br from-slate-950/90 to-slate-900/90 border border-slate-800/60 rounded-xl overflow-hidden hover:border-blue-500/40 transition-all shadow-lg" data-id="element-1615">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1616" />
                  <div className="relative p-6" data-id="element-1617">
                    <div className="flex items-center justify-between mb-4" data-id="element-1618">
                      <div className="flex items-center gap-3" data-id="element-1619">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center" data-id="element-1620">
                          <MessageSquareIcon className="w-5 h-5 text-blue-400" data-id="element-1621" />
                        </div>
                        <div data-id="element-1622">
                          <h4 className="text-lg font-bold text-white" data-id="element-1623">
                            {externalBot.name}
                          </h4>
                          <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded-md font-bold" data-id="element-1624">
                            External
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-400 mb-4" data-id="element-1625">
                      {externalBot.purpose}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-1626">
                      <button onClick={() => window.open(externalBot.link, '_blank')} className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-bold rounded-lg transition-all shadow-lg shadow-blue-600/30 flex items-center gap-2" data-id="element-1627">
                        <ExternalLinkIcon className="w-4 h-4" data-id="element-1628" />
                        Open Bot
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1629">
                      {externalBot.bound_spaces.map(space => <span key={space.id} className="text-xs px-3 py-1.5 bg-slate-800/60 text-slate-300 rounded-md border border-slate-700/40 flex items-center gap-1.5" data-id="element-1630">
                          <DatabaseIcon className="w-3 h-3 text-blue-400" data-id="element-1631" />
                          {space.name}
                        </span>)}
                    </div>
                  </div>
                </div>}
              {internalBot && <div className="group relative bg-gradient-to-br from-slate-950/90 to-slate-900/90 border border-slate-800/60 rounded-xl overflow-hidden hover:border-purple-500/40 transition-all shadow-lg" data-id="element-1632">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1633" />
                  <div className="relative p-6" data-id="element-1634">
                    <div className="flex items-center justify-between mb-4" data-id="element-1635">
                      <div className="flex items-center gap-3" data-id="element-1636">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center" data-id="element-1637">
                          <MessageSquareIcon className="w-5 h-5 text-purple-400" data-id="element-1638" />
                        </div>
                        <div data-id="element-1639">
                          <h4 className="text-lg font-bold text-white" data-id="element-1640">
                            {internalBot.name}
                          </h4>
                          <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded-md font-bold" data-id="element-1641">
                            Internal
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-400 mb-4" data-id="element-1642">
                      {internalBot.purpose}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-1643">
                      <button onClick={() => window.open(internalBot.link, '_blank')} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white text-sm font-bold rounded-lg transition-all shadow-lg shadow-purple-600/30 flex items-center gap-2" data-id="element-1644">
                        <ExternalLinkIcon className="w-4 h-4" data-id="element-1645" />
                        Open Bot
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1646">
                      {internalBot.bound_spaces.map(space => <span key={space.id} className="text-xs px-3 py-1.5 bg-slate-800/60 text-slate-300 rounded-md border border-slate-700/40 flex items-center gap-1.5" data-id="element-1647">
                          <DatabaseIcon className="w-3 h-3 text-purple-400" data-id="element-1648" />
                          {space.name}
                        </span>)}
                    </div>
                  </div>
                </div>}
            </div>
          </motion.div>}
        {activeTab === 'team' && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-6" data-id="element-1649">
            {/* Quad Team */}
            {quadTeam.length > 0 && <div data-id="element-1650">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-1651">
                  <UsersIcon className="w-5 h-5 text-blue-400" data-id="element-1652" />
                  Quad Team ({quadTeam.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1653">
                  {quadTeam.map(person => <button key={person.id} onClick={() => setSelectedPerson({
              ...person,
              type: 'quad',
              accountName: account.name,
              accountLogo: account.logo_url
            })} className="group bg-gradient-to-br from-slate-900/90 to-slate-950/90 border border-slate-800/60 rounded-xl p-5 hover:border-blue-500/40 transition-all shadow-lg text-left hover:scale-[1.02] cursor-pointer" data-id="element-1654">
                      <div className="flex items-start gap-4" data-id="element-1655">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white text-sm font-bold shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform" data-id="element-1656">
                          {person.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-1657">
                          <h4 className="text-base font-semibold text-white mb-1 group-hover:text-blue-400 transition-colors" data-id="element-1658">
                            {person.name}
                          </h4>
                          <p className="text-sm text-slate-400 mb-2" data-id="element-1659">
                            {person.role}
                          </p>
                          {person.email && <div className="flex items-center gap-2 text-xs text-slate-500 mb-1" data-id="element-1660">
                              <MailIcon className="w-3 h-3" data-id="element-1661" />
                              <span className="truncate" data-id="element-1662">{person.email}</span>
                            </div>}
                          {person.phone && <div className="flex items-center gap-2 text-xs text-slate-500 mb-1" data-id="element-1663">
                              <PhoneIcon className="w-3 h-3" data-id="element-1664" />
                              <span data-id="element-1665">{person.phone}</span>
                            </div>}
                          {person.location && <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-1666">
                              <MapPinIcon className="w-3 h-3" data-id="element-1667" />
                              <span data-id="element-1668">{person.location}</span>
                            </div>}
                        </div>
                      </div>
                    </button>)}
                </div>
              </div>}
            {/* Client Contacts */}
            {clientContacts.length > 0 && <div data-id="element-1669">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-1670">
                  <BuildingIcon className="w-5 h-5 text-purple-400" data-id="element-1671" />
                  Client Contacts ({clientContacts.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1672">
                  {clientContacts.map(person => <button key={person.id} onClick={() => setSelectedPerson({
              ...person,
              type: 'client',
              accountName: account.name,
              accountLogo: account.logo_url
            })} className="group bg-gradient-to-br from-slate-900/90 to-slate-950/90 border border-slate-800/60 rounded-xl p-5 hover:border-purple-500/40 transition-all shadow-lg text-left hover:scale-[1.02] cursor-pointer" data-id="element-1673">
                      <div className="flex items-start gap-4" data-id="element-1674">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-sm font-bold shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform" data-id="element-1675">
                          {person.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-1676">
                          <h4 className="text-base font-semibold text-white mb-1 group-hover:text-purple-400 transition-colors" data-id="element-1677">
                            {person.name}
                          </h4>
                          <p className="text-sm text-slate-400 mb-2" data-id="element-1678">
                            {person.role}
                          </p>
                          {person.email && <div className="flex items-center gap-2 text-xs text-slate-500 mb-1" data-id="element-1679">
                              <MailIcon className="w-3 h-3" data-id="element-1680" />
                              <span className="truncate" data-id="element-1681">{person.email}</span>
                            </div>}
                          {person.phone && <div className="flex items-center gap-2 text-xs text-slate-500 mb-1" data-id="element-1682">
                              <PhoneIcon className="w-3 h-3" data-id="element-1683" />
                              <span data-id="element-1684">{person.phone}</span>
                            </div>}
                          {person.location && <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-1685">
                              <MapPinIcon className="w-3 h-3" data-id="element-1686" />
                              <span data-id="element-1687">{person.location}</span>
                            </div>}
                        </div>
                      </div>
                    </button>)}
                </div>
              </div>}
          </motion.div>}
        {activeTab === 'assets' && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-6" data-id="element-1688">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-1689">
              <FolderIcon className="w-5 h-5 text-amber-400" data-id="element-1690" />
              Documents & Assets ({account.assets.length})
            </h3>
            {account.assets.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1691">
                {account.assets.map(asset => <div key={asset.id} className="group bg-gradient-to-br from-slate-900/90 to-slate-950/90 border border-slate-800/60 rounded-xl p-5 hover:border-amber-500/40 transition-all shadow-lg" data-id="element-1692">
                    <div className="flex items-start gap-4" data-id="element-1693">
                      <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0" data-id="element-1694">
                        <FileTextIcon className="w-6 h-6 text-amber-400" data-id="element-1695" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1696">
                        <h4 className="text-base font-semibold text-white mb-1" data-id="element-1697">
                          {asset.title}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-3" data-id="element-1698">
                          <span className="px-2 py-1 bg-slate-800/60 rounded" data-id="element-1699">
                            {asset.kind}
                          </span>
                          <CalendarIcon className="w-3 h-3" data-id="element-1700" />
                          <span data-id="element-1701">
                            {new Date(asset.last_updated).toLocaleDateString()}
                          </span>
                        </div>
                        <button onClick={() => window.open(asset.url, '_blank')} className="text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1" data-id="element-1702">
                          Open Document
                          <ExternalLinkIcon className="w-3 h-3" data-id="element-1703" />
                        </button>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="text-center py-12 bg-gradient-to-br from-slate-900/60 to-slate-950/60 border border-slate-800/60 rounded-xl" data-id="element-1704">
                <FileTextIcon className="w-12 h-12 text-slate-600 mx-auto mb-3" data-id="element-1705" />
                <p className="text-slate-400" data-id="element-1706">No assets available</p>
              </div>}
          </motion.div>}
      </div>
      {/* Person Detail Modal */}
      <AnimatePresence data-id="element-1707">
        {selectedPerson && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSelectedPerson(null)} className="fixed inset-0 bg-black/75 backdrop-blur-md z-[100]" data-id="element-1708" />
            <div className="fixed inset-0 z-[101] overflow-y-auto" data-id="element-1709">
              <div className="flex min-h-full items-center justify-center p-4" data-id="element-1710">
                <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-slate-800/80 rounded-3xl shadow-2xl overflow-hidden" data-id="element-1711">
                  {/* Modal Header */}
                  <div className="relative px-8 py-6 border-b border-slate-800/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950" data-id="element-1712">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-transparent to-blue-500/5 pointer-events-none" data-id="element-1713" />
                    <div className="relative flex items-start justify-between" data-id="element-1714">
                      <div className="flex items-center gap-4" data-id="element-1715">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white text-2xl font-bold shadow-2xl ring-2 ring-purple-500/30" data-id="element-1716">
                          {selectedPerson.name.charAt(0)}
                        </div>
                        <div data-id="element-1717">
                          <h2 className="text-2xl font-bold text-white mb-1 bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent" data-id="element-1718">
                            {selectedPerson.name}
                          </h2>
                          <p className="text-slate-400 flex items-center gap-2" data-id="element-1719">
                            <BriefcaseIcon className="w-4 h-4" data-id="element-1720" />
                            {selectedPerson.role}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setSelectedPerson(null)} className="p-2.5 hover:bg-slate-800/60 rounded-xl transition-all text-slate-400 hover:text-white hover:scale-105" data-id="element-1721">
                        <XIcon className="w-6 h-6" data-id="element-1722" />
                      </button>
                    </div>
                  </div>
                  {/* Modal Content */}
                  <div className="p-8 space-y-6 max-h-[calc(85vh-140px)] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-1723">
                    {/* Type Badge */}
                    <div className={`inline-flex px-4 py-2 rounded-xl text-sm font-bold border shadow-lg ${selectedPerson.type === 'client' ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' : 'bg-purple-500/10 border-purple-500/30 text-purple-400'}`} data-id="element-1724">
                      {selectedPerson.type === 'client' ? 'Client Contact' : 'Quad Team Member'}
                    </div>
                    {/* Contact Information */}
                    <div className="space-y-4" data-id="element-1725">
                      <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-1726">
                        Contact Information
                      </h3>
                      <div className="space-y-3" data-id="element-1727">
                        <div className="flex items-center gap-3 p-4 bg-slate-900/60 border border-slate-800/60 rounded-xl" data-id="element-1728">
                          <MailIcon className="w-5 h-5 text-blue-400 flex-shrink-0" data-id="element-1729" />
                          <div data-id="element-1730">
                            <p className="text-xs text-slate-500 mb-1" data-id="element-1731">Email</p>
                            <a href={`mailto:${selectedPerson.email}`} className="text-sm text-white hover:text-blue-400 transition-colors" data-id="element-1732">
                              {selectedPerson.email}
                            </a>
                          </div>
                        </div>
                        {selectedPerson.phone && <div className="flex items-center gap-3 p-4 bg-slate-900/60 border border-slate-800/60 rounded-xl" data-id="element-1733">
                            <PhoneIcon className="w-5 h-5 text-green-400 flex-shrink-0" data-id="element-1734" />
                            <div data-id="element-1735">
                              <p className="text-xs text-slate-500 mb-1" data-id="element-1736">
                                Phone
                              </p>
                              <a href={`tel:${selectedPerson.phone}`} className="text-sm text-white hover:text-green-400 transition-colors" data-id="element-1737">
                                {selectedPerson.phone}
                              </a>
                            </div>
                          </div>}
                        {selectedPerson.location && <div className="flex items-center gap-3 p-4 bg-slate-900/60 border border-slate-800/60 rounded-xl" data-id="element-1738">
                            <MapPinIcon className="w-5 h-5 text-purple-400 flex-shrink-0" data-id="element-1739" />
                            <div data-id="element-1740">
                              <p className="text-xs text-slate-500 mb-1" data-id="element-1741">
                                Location
                              </p>
                              <p className="text-sm text-white" data-id="element-1742">
                                {selectedPerson.location}
                              </p>
                            </div>
                          </div>}
                      </div>
                    </div>
                    {/* Account Information */}
                    <div className="space-y-4" data-id="element-1743">
                      <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-1744">
                        Account
                      </h3>
                      <div className="flex items-center gap-4 p-5 bg-slate-900/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all" data-id="element-1745">
                        <div className="w-14 h-14 rounded-xl bg-white/95 p-2.5 flex items-center justify-center shadow-lg flex-shrink-0" data-id="element-1746">
                          <img src={selectedPerson.accountLogo} alt={selectedPerson.accountName} className="w-full h-full object-contain" data-id="element-1747" />
                        </div>
                        <div data-id="element-1748">
                          <p className="text-sm font-semibold text-white mb-1" data-id="element-1749">
                            {selectedPerson.accountName}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-1750">
                            Client Account
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </>}
      </AnimatePresence>
    </>;
}